import React, { useContext } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import SparkLine from '@prisjakt/ui-sparkline';
import Rating from '@prisjakt/ui-rating';
import Currency from '@prisjakt/ui-currency';
import * as S from './styles';
import { ProductPageQuery } from '../../../../types';

/* Scroll user to top of page and if neccessary, perform sidescroll
    in tab. Will not work on initial render but on all clicks
    Silent error on fail (not all browsers supports smooth scroll)
*/
const handleScroll = (e: React.MouseEvent<HTMLButtonElement>): void => {
  try {
    window.scrollTo(0, 0);

    if (!e.currentTarget.parentElement) {
      return;
    }

    const tabList = e.currentTarget.parentElement;
    const { offsetWidth, offsetLeft } = e.currentTarget;

    const tabNotVisible =
      offsetWidth + offsetLeft > offsetWidth || tabList.scrollLeft > offsetLeft;

    if (tabNotVisible) {
      tabList.scrollTo({
        top: 0,
        left: offsetLeft,
        behavior: 'smooth',
      });
    }
  } catch (err) {} // eslint-disable-line no-empty
};

type TabListProps = {
  isSticky: boolean;
  product: ProductPageQuery['product'];
  routeSection: 'main' | 'properties' | 'reviews' | 'statistics';
};

const TabList = ({ isSticky, routeSection, product }: TabListProps) => {
  const { t, linkTo } = useContext(AppContext);

  if (!product) {
    return null;
  }

  return (
    <S.TabList isSticky={isSticky}>
      <S.Tab
        active={routeSection === 'main'}
        to={linkTo('product', product)}
        // @ts-ignore
        onClick={handleScroll}
        data-test="TabButton"
        data-test-section="main"
        minWidth={90}
      >
        <S.TabTitle>{t('product', 'tabs.prices')}</S.TabTitle>
        <S.TabLowestPrice>
          {product.priceSummary && (
            <Currency
              price={
                product.priceSummary.regular || product.priceSummary.alternative
              }
            />
          )}
        </S.TabLowestPrice>
      </S.Tab>

      <S.Tab
        active={routeSection === 'properties'}
        to={linkTo('productProperties', product)}
        // @ts-ignore
        onClick={handleScroll}
        data-test="TabButton"
        data-test-section="properties"
        minWidth={100}
      >
        <S.TabTitle>{t('product', 'tabs.properties')}</S.TabTitle>
        <S.TabSubtitle>
          {product.coreProperties && product.coreProperties.nodes && (
            <S.StyledCoreProperties
              toneDown
              view="list"
              size="tiny"
              maxrows={2}
              properties={product.coreProperties.nodes
                .map(node => node.pretty || '')
                .filter(Boolean)}
            />
          )}
        </S.TabSubtitle>
      </S.Tab>

      <S.Tab
        active={routeSection === 'reviews'}
        to={linkTo('productReviews', product)}
        // @ts-ignore
        onClick={handleScroll}
        data-test="TabButton"
        data-test-section="reviews"
        minWidth={145}
      >
        <S.TabTitle>{t('product', 'tabs.reviews')}</S.TabTitle>
        <S.TabRating>
          {product.userReviewSummary &&
          product.userReviewSummary.count &&
          product.userReviewSummary.count > 0 ? (
            <Rating
              rate={product.userReviewSummary.rating}
              count={product.userReviewSummary.count}
              size="small"
              showRate
              toneDown
              showCounterAs="number"
              showRateAs="number"
            />
          ) : null}
        </S.TabRating>
      </S.Tab>

      <S.Tab
        active={routeSection === 'statistics'}
        to={linkTo('productPriceHistory', product)}
        // @ts-ignore
        onClick={handleScroll}
        data-test="TabButton"
        data-test-section="statistics"
        minWidth={100}
      >
        <S.TabTitle>{t('product', 'tabs.price-history')}</S.TabTitle>
        <S.TabSparkline>
          {product.sparkline && product.sparkline.values && (
            <SparkLine
              width={40}
              height={10}
              values={product.sparkline.values}
              disableAnimation
            />
          )}
        </S.TabSparkline>
      </S.Tab>
    </S.TabList>
  );
};

export default TabList;
